import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout, { KPICard, StatusBadge } from "../../components/admin/AdminLayout";
import { listEmails, emailsStats, EMAIL_TYPE_OPTIONS, EMAIL_STATUS } from "../../lib/admin-api";
import { Mail, Search, AlertTriangle, CheckCircle2, Clock, RefreshCw, X } from "lucide-react";
import { Input } from "../../components/ui/input";

const PAGE_SIZE = 25;

export default function AdminEmails() {
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [q, setQ] = useState("");
    const [emailType, setEmailType] = useState("");
    const [estado, setEstado] = useState("");
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const [s, list] = await Promise.all([
                    emailsStats(),
                    listEmails({ limit: PAGE_SIZE, skip, q, email_type: emailType || undefined, estado: estado || undefined }),
                ]);
                if (!alive) return;
                setStats(s);
                setItems(list.items || []);
                setTotal(list.total || 0);
            } catch (e) {
                console.error(e);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [skip, q, emailType, estado]);

    return (
        <AdminLayout title="Email log" subtitle="Histórico de correos disparados por el sistema" testid="admin-emails-page">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <KPICard label="Total" value={stats?.total ?? "—"} icon={Mail} accent="text-eclipse-cta" testid="emails-kpi-total" />
                <KPICard label="Enviados" value={stats?.sent ?? "—"} icon={CheckCircle2} accent="text-green-600" testid="emails-kpi-sent" />
                <KPICard label="Fallidos" value={stats?.failed ?? "—"} icon={AlertTriangle} accent="text-red-600" testid="emails-kpi-failed" />
                <KPICard label="Skipped" value={stats?.skipped ?? "—"} icon={Clock} accent="text-neutral-500" testid="emails-kpi-skipped" />
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-4 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                    <label className="text-[11px] text-neutral-500 uppercase tracking-wider">Buscar</label>
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <Input
                            data-testid="emails-search"
                            value={q}
                            onChange={(e) => { setSkip(0); setQ(e.target.value); }}
                            placeholder="Email o asunto"
                            className="pl-9"
                        />
                    </div>
                </div>
                <div>
                    <label className="text-[11px] text-neutral-500 uppercase tracking-wider block mb-1">Tipo</label>
                    <select data-testid="emails-filter-type" value={emailType} onChange={(e) => { setSkip(0); setEmailType(e.target.value); }} className="h-9 border border-neutral-200 rounded-md px-3 text-sm bg-white">
                        <option value="">Todos</option>
                        {EMAIL_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[11px] text-neutral-500 uppercase tracking-wider block mb-1">Estado</label>
                    <select data-testid="emails-filter-estado" value={estado} onChange={(e) => { setSkip(0); setEstado(e.target.value); }} className="h-9 border border-neutral-200 rounded-md px-3 text-sm bg-white">
                        <option value="">Todos</option>
                        <option value="sent">Enviado</option>
                        <option value="failed">Fallido</option>
                        <option value="skipped">Skipped</option>
                    </select>
                </div>
                <button onClick={() => setSkip(0)} className="h-9 px-3 border border-neutral-200 rounded-md text-sm flex items-center gap-2 hover:bg-neutral-50" data-testid="emails-refresh">
                    <RefreshCw className="w-4 h-4" /> Refrescar
                </button>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-neutral-600 text-[11px] uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3">Fecha</th>
                                <th className="text-left px-4 py-3">Destinatario</th>
                                <th className="text-left px-4 py-3">Asunto</th>
                                <th className="text-left px-4 py-3">Tipo</th>
                                <th className="text-left px-4 py-3">Estado</th>
                                <th className="text-left px-4 py-3">Origen</th>
                                <th className="text-left px-4 py-3">Lead</th>
                                <th className="text-right px-4 py-3">Ver</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && items.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-12 text-neutral-400">Cargando…</td></tr>
                            )}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-12 text-neutral-400">Sin correos</td></tr>
                            )}
                            {items.map((e) => (
                                <tr key={e.id} className="border-t border-neutral-100 hover:bg-neutral-50/40" data-testid={`email-row-${e.id}`}>
                                    <td className="px-4 py-2.5 text-neutral-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</td>
                                    <td className="px-4 py-2.5 font-medium text-neutral-900">{e.destinatario || "—"}</td>
                                    <td className="px-4 py-2.5 max-w-xs truncate text-neutral-700">{e.asunto || "—"}</td>
                                    <td className="px-4 py-2.5 text-neutral-600 text-xs">{e.email_type}</td>
                                    <td className="px-4 py-2.5"><StatusBadge label={EMAIL_STATUS[e.estado]?.label || e.estado} color={EMAIL_STATUS[e.estado]?.color || "bg-neutral-100 text-neutral-600"} /></td>
                                    <td className="px-4 py-2.5 text-neutral-500 text-xs">{e.origen || "—"}</td>
                                    <td className="px-4 py-2.5 text-xs">
                                        {e.lead_id ? <Link to={`/admin/leads/${e.lead_id}`} className="text-eclipse-cta hover:underline">Ver lead</Link> : "—"}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <button onClick={() => setPreview(e)} className="text-xs underline text-neutral-700 hover:text-eclipse-cta" data-testid={`email-preview-${e.id}`}>preview</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-100 text-xs text-neutral-500">
                    <div>Mostrando {items.length === 0 ? 0 : skip + 1}-{Math.min(skip + PAGE_SIZE, total)} de {total}</div>
                    <div className="flex gap-2">
                        <button onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} disabled={skip === 0} className="px-3 py-1.5 border border-neutral-200 rounded-md disabled:opacity-40" data-testid="emails-page-prev">Anterior</button>
                        <button onClick={() => setSkip(skip + PAGE_SIZE)} disabled={skip + PAGE_SIZE >= total} className="px-3 py-1.5 border border-neutral-200 rounded-md disabled:opacity-40" data-testid="emails-page-next">Siguiente</button>
                    </div>
                </div>
            </div>

            {preview && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
                    <div className="bg-white max-w-3xl w-full max-h-[88vh] rounded-md overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="email-preview-modal">
                        <div className="px-5 py-4 border-b flex items-center justify-between">
                            <div>
                                <div className="text-xs text-neutral-500">{preview.email_type} · {new Date(preview.created_at).toLocaleString()}</div>
                                <div className="font-semibold text-neutral-900 mt-0.5">{preview.asunto}</div>
                                <div className="text-xs text-neutral-500">para {preview.destinatario}</div>
                            </div>
                            <button onClick={() => setPreview(null)} className="p-2 hover:bg-neutral-100 rounded-md"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 overflow-auto p-5 bg-neutral-50 text-sm">
                            {preview.error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4 text-xs"><strong>Error:</strong> {preview.error}</div>}
                            <div className="text-xs text-neutral-500 mb-2">Estado: <strong>{preview.estado}</strong> · Origen: {preview.origen}</div>
                            <pre className="whitespace-pre-wrap text-xs text-neutral-700">{JSON.stringify({ id: preview.id, lead_id: preview.lead_id, campaign_id: preview.campaign_id, resend_id: preview.resend_id }, null, 2)}</pre>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}
